import React from "react"
import { Accordion as BSAccordion } from "react-bootstrap"
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import { ProductConfiguratorOptionPayload } from "shared/types/ProductConfigurator"
import { TabType } from "shared/types/Tab"

import { AddonAccordionToggle } from "widgets/product-configurator/ui/addons/AddonAccordionToggle"
import { AddonDisplayProps } from "widgets/product-configurator/ui/addons/AddonDisplay.types"
import { ProductConfiguratorAddonOptionRenderer } from "widgets/product-configurator/ui/addons/ProductConfiguratorAddonOptionRenderer"

import "../../styles/_addon-accordion.scss"
import { useAddonsAccordionViewModel } from "./hooks/useAddonsAccordionViewModel"

export interface AddonAccordionProps extends AddonDisplayProps {
  isFullWidth?: boolean
}

const emptyPayload = {}

interface AddonAccordionItemProps {
  tab: TabType<ProductConfiguratorOptionPayload>
  activeKey: AccordionEventKey
  isSelected?: boolean
  isDisabled?: boolean
  onSelect: (key: string) => void
  onExpand: (key: AccordionEventKey) => void
}

const AddonAccordionItem = React.memo(
  ({
    tab,
    activeKey,
    isSelected,
    isDisabled,
    onSelect,
    onExpand
  }: AddonAccordionItemProps) => {
    const { key: eventKey, title, content, className: tabClassName } = tab
    const isExpanded = Array.isArray(activeKey)
      ? activeKey.includes(eventKey)
      : activeKey === eventKey

    const handleSelect = useEvent(() => {
      onSelect(eventKey)
    })
    const handleExpand = useEvent(() => {
      onExpand(eventKey)
    })

    return (
      <BSAccordion.Item
        id={eventKey}
        eventKey={eventKey}
        className={classNames(
          tabClassName,
          "addon-accordion__item",
          "card",
          "card--elevated",
          { "no-content": !content }
        )}
      >
        <div className="accordion-button accordion-button-flush addon-accordion__header">
          <AddonAccordionToggle
            addonKey={eventKey}
            title={typeof title === "string" ? title : undefined}
            isSelected={isSelected}
            isDisabled={isDisabled}
            onToggle={handleSelect}
          />
          <ProductConfiguratorAddonOptionRenderer
            option={tab}
            payload={emptyPayload}
            isSelected={isSelected}
            isExpandable={!!content}
            isExpanded={isExpanded}
            isDisabled={isDisabled}
            aria-controls={`${eventKey}-body`}
            onExpand={handleExpand}
          />
        </div>
        {content ? (
          <BSAccordion.Body id={`${eventKey}-body`}>{content}</BSAccordion.Body>
        ) : null}
      </BSAccordion.Item>
    )
  }
)

export const AddonAccordion = React.memo((props: AddonAccordionProps) => {
  const { activeKey, onSelect, onExpand, tabs, selectedTabs, isDisabled } =
    useAddonsAccordionViewModel(props)

  const handleExpand = useEvent((nextActiveKey: AccordionEventKey) => {
    if (activeKey === nextActiveKey) {
      onExpand(null)
    } else {
      onExpand(nextActiveKey)
    }
  })

  const className = classNames(
    "addon-accordion",
    "product-configurator-accordion",
    props.className
  )

  return (
    <BSAccordion
      flush
      activeKey={activeKey}
      onSelect={handleExpand}
      className={className}
    >
      {tabs.map(tab => (
        <AddonAccordionItem
          key={tab.key}
          tab={tab}
          activeKey={activeKey}
          isSelected={selectedTabs?.includes(tab.key)}
          isDisabled={isDisabled}
          onSelect={onSelect}
          onExpand={handleExpand}
        />
      ))}
    </BSAccordion>
  )
})
